/**
 * Centrale logica voor de "Orders Decoprof" feature.
 * Alle exclude-lijsten, type-mappings en pakbon-URL helpers staan hier,
 * zodat aanpassingen geïsoleerd zijn.
 *
 * NB: toegangscontrole loopt via het standaard role_permissions-systeem
 * (Resource = "ordersDecoprof") en NIET meer via een per-user feature flag.
 * Zie src/hooks/rollenbeheer/useRolePermissions.ts → hasPermission("ordersDecoprof", "read").
 */

// ----- Filter: welke leveranciers zijn UITGESLOTEN ---------------------------

/**
 * Leveranciers die expliciet NIET als "Decoprof-order" tellen.
 * Een order wordt getoond zodra minstens één regel een leverancier
 * heeft die hier NIET in voorkomt. Nieuwe leveranciers verschijnen
 * dus automatisch.
 */
export const ORDERS_DECOPROF_EXCLUDED_SUPPLIERS: ReadonlySet<string> = new Set([
  "Luijten-VVZ BV",
  "Delta Paints BV",
]);

export function isExcludedSupplier(supplier: string | null | undefined): boolean {
  if (!supplier) return false;
  return ORDERS_DECOPROF_EXCLUDED_SUPPLIERS.has(supplier);
}

// ----- Type-mapping (leverancier -> "Type order") ---------------------------

export type OrderDecoprofType =
  | "Verfbestelling"
  | "Behangorder"
  | "Spuitbusbestelling"
  | "Overige";

/**
 * Bekende leveranciers — altijd zichtbaar in de Leverancier-filter dropdown,
 * ook als er momenteel 0 orders voor zijn. Houd in sync met SUPPLIER_TO_TYPE.
 */
export const KNOWN_SUPPLIERS: ReadonlyArray<string> = [
  "Akzo Nobel Decorative Coatings",
  "Arte International",
  "Baten Trading Company",
  "Decoprof bv",
  "Dutch Walltextile Company",
  "Eijffinger",
  "Intervos",
  "Mooij Verf BV",
  "Nature at Home",
  "Sikkens Center en Simonis",
];

const SUPPLIER_TO_TYPE: Record<string, OrderDecoprofType> = {
  "Akzo Nobel Decorative Coatings": "Verfbestelling",
  "Arte International": "Behangorder",
  "Baten Trading Company": "Spuitbusbestelling",
  "Decoprof bv": "Overige",
  "Dutch Walltextile Company": "Behangorder",
  "Eijffinger": "Behangorder",
  "Intervos": "Behangorder",
  "Mooij Verf BV": "Verfbestelling",
  "Nature at Home": "Behangorder",
  "Sikkens Center en Simonis": "Verfbestelling",
};

export function mapSupplierToType(
  supplier: string | null | undefined,
): OrderDecoprofType {
  if (!supplier) return "Overige";
  return SUPPLIER_TO_TYPE[supplier] ?? "Overige";
}

/**
 * R57 — FIX 4: alle unieke types voor een order, gebaseerd op ALLE
 * non-excluded suppliers. Mixed orders (bv. Mooij Verf + Intervos + Akzo)
 * geven 2-3 verschillende type-badges. Alfabetisch gesorteerd voor
 * deterministische rendering.
 */
/**
 * R73 — alle unieke leveranciers van een order, ge-excluded suppliers eruit
 * gefilterd. Alfabetisch (NL) gesorteerd voor deterministische rendering.
 * Hergebruikt dezelfde aanpak als getOrderTypes (R57).
 */
export function getOrderSuppliers(suppliers: ReadonlyArray<string>): string[] {
  const set = new Set<string>();
  for (const s of suppliers) {
    if (!s) continue;
    if (ORDERS_DECOPROF_EXCLUDED_SUPPLIERS.has(s)) continue;
    set.add(s);
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b, "nl"));
}

export function getOrderTypes(suppliers: ReadonlyArray<string>): OrderDecoprofType[] {
  const types = new Set<OrderDecoprofType>();
  for (const s of suppliers) {
    if (!s) continue;
    if (ORDERS_DECOPROF_EXCLUDED_SUPPLIERS.has(s)) continue;
    types.add(mapSupplierToType(s));
  }
  if (types.size === 0) types.add("Overige");
  // R74: filter "Overige" weg zodra er een specifieker type aanwezig is.
  if (types.size > 1 && types.has("Overige")) types.delete("Overige");
  return Array.from(types).sort((a, b) => a.localeCompare(b, "nl"));
}

export const ORDER_DECOPROF_TYPE_OPTIONS: ReadonlyArray<OrderDecoprofType> = [
  "Verfbestelling",
  "Behangorder",
  "Spuitbusbestelling",
  "Overige",
];

// ----- Status ---------------------------------------------------------------

export type OrderDecoprofStatus = "Nieuw" | "Uitgeprint" | "Aangemaakt";

export const ORDER_DECOPROF_STATUSES: ReadonlyArray<OrderDecoprofStatus> = [
  "Nieuw",
  "Uitgeprint",
  "Aangemaakt",
];

// ----- Pakbon: server-side gegenereerde PDF via edge function ---------------
// BUILD=R27-merged-pdf-batch

/**
 * R45: iOS-safe PDF delivery voor batch-pakbon.
 *
 * Probleem op iOS Safari:
 *   - <a download> met blob: URL wordt vaak genegeerd of opent als gewone link.
 *   - window.open(blobUrl) NA een await wordt geblokkeerd (popup-block buiten user-gesture).
 *
 * Oplossing:
 *   - De caller MOET in dezelfde click-handler `window.open('about:blank','_blank')`
 *     synchroon openen en het Window-object via `preOpenedWindow` doorgeven.
 *   - Na fetch zetten we `preOpenedWindow.location.href = blobUrl` → werkt op iOS,
 *     omdat het bestaande window al door de gebruiker geopend is.
 *   - Op desktop blijft <a download> werken; we gebruiken het pre-opened window
 *     alleen wanneer beschikbaar.
 */
export async function printPackingSlipsBatch(
  orderids: number[],
  preOpenedWindow?: Window | null,
): Promise<{
  succeededOrderids: number[];
  failedOrderids: number[];
  failedReasons: Record<string, string>;
}> {
  const sessionToken =
    typeof window !== "undefined" ? localStorage.getItem("auth_session_token") : null;
  if (!sessionToken) {
    if (preOpenedWindow) try { preOpenedWindow.close(); } catch { /* noop */ }
    throw new Error("Niet ingelogd (geen sessie token)");
  }
  if (orderids.length === 0) {
    if (preOpenedWindow) try { preOpenedWindow.close(); } catch { /* noop */ }
    throw new Error("Geen orders geselecteerd");
  }

  const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
  const url = `https://${projectId}.supabase.co/functions/v1/print-decoprof-packing-slips-batch`;

  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "(no-ua)";
  console.log(`[R45 batch-print] fetch — sending N=${orderids.length} ids=[${orderids.join(",")}] ua="${ua}" preOpened=${!!preOpenedWindow}`);
  const ac = new AbortController();
  const timeoutId = setTimeout(() => ac.abort(), 90_000);
  console.time(`[batch-print] fetch ${orderids.length} orders`);
  let res: Response;
  try {
    res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-session-token": sessionToken,
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      },
      body: JSON.stringify({ orderids }),
      signal: ac.signal,
    });
  } catch (e: any) {
    if (preOpenedWindow) try { preOpenedWindow.close(); } catch { /* noop */ }
    if (e?.name === "AbortError") throw new Error("Pakbon-batch duurde te lang (>90s) — probeer minder orders tegelijk");
    throw e;
  } finally {
    clearTimeout(timeoutId);
    console.timeEnd(`[batch-print] fetch ${orderids.length} orders`);
  }

  if (!res.ok) {
    if (preOpenedWindow) try { preOpenedWindow.close(); } catch { /* noop */ }
    let msg = `HTTP ${res.status}`;
    let failedReasons: Record<string, string> = {};
    try {
      const j = await res.json();
      msg = j?.error || msg;
      if (j?.failedReasons && typeof j.failedReasons === "object") {
        failedReasons = j.failedReasons;
      }
    } catch { /* ignore */ }
    const err = new Error(msg);
    (err as any).failedReasons = failedReasons;
    throw err;
  }

  const succeededHeader = res.headers.get("x-succeeded-orderids") || "";
  const failedHeader = res.headers.get("x-failed-orderids") || "";
  const reasonsHeader = res.headers.get("x-failed-reasons") || "";
  const parseCsv = (s: string) =>
    s.split(",").map((x) => Number(x.trim())).filter((n) => Number.isFinite(n) && n > 0);
  const succeededOrderids = parseCsv(succeededHeader);
  const failedOrderids = parseCsv(failedHeader);
  let failedReasons: Record<string, string> = {};
  if (reasonsHeader) {
    try { failedReasons = JSON.parse(reasonsHeader); } catch { /* ignore */ }
  }

  const blob = await res.blob();
  if (blob.type && !blob.type.includes("pdf")) {
    if (preOpenedWindow) try { preOpenedWindow.close(); } catch { /* noop */ }
    throw new Error("Onverwacht response-type van pakbon-batch-functie");
  }

  const cd = res.headers.get("content-disposition") || "";
  const match = /filename="?([^"]+)"?/i.exec(cd);
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const fallbackName =
    `pakbonnen-decoprof-${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
    `-${pad(now.getHours())}${pad(now.getMinutes())}.pdf`;
  const fileName = match?.[1] || fallbackName;

  const blobUrl = URL.createObjectURL(blob);
  const cleanup = () => setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);

  // R45: iOS Safari → gebruik pre-opened window (blijft binnen user-gesture).
  if (isMobileDevice() && preOpenedWindow && !preOpenedWindow.closed) {
    console.log(`[R45 batch-print] iOS-pad: navigeer pre-opened window naar blob (${blob.size} bytes)`);
    try {
      preOpenedWindow.location.href = blobUrl;
      cleanup();
      return { succeededOrderids, failedOrderids, failedReasons };
    } catch (e) {
      console.warn("[R45 batch-print] pre-opened window navigatie faalde, fallback naar <a download>", e);
    }
  }

  // Desktop / fallback: native download via <a download>.
  if (preOpenedWindow) try { preOpenedWindow.close(); } catch { /* noop */ }
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = fileName;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    try { document.body.removeChild(a); } catch { /* ignore */ }
    cleanup();
  }, 0);

  return { succeededOrderids, failedOrderids, failedReasons };
}



export function isMobileDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

/**
 * Roept de edge function aan die zelf de pakbon-PDF rendert.
 *
 * MOBIEL: triggert native download via onzichtbare <a download>.
 * DESKTOP: opent in nieuwe tab. Om iOS-popup-blocker te omzeilen MOET de
 * caller bij de click-handler SYNCHROON `window.open('about:blank','_blank')`
 * aanroepen en het resultaat via `preOpenedWindow` doorgeven.
 */
export async function openPackingSlip(
  orderid: number,
  preOpenedWindow?: Window | null,
): Promise<void> {
  const sessionToken =
    typeof window !== "undefined" ? localStorage.getItem("auth_session_token") : null;
  if (!sessionToken) {
    if (preOpenedWindow) preOpenedWindow.close();
    throw new Error("Niet ingelogd (geen sessie token)");
  }

  const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
  const url = `https://${projectId}.supabase.co/functions/v1/print-decoprof-packing-slip`;

  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-session-token": sessionToken,
      apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
    },
    body: JSON.stringify({ orderid }),
  });

  if (!res.ok) {
    if (preOpenedWindow) preOpenedWindow.close();
    let msg = `HTTP ${res.status}`;
    let retryAfterMs: number | undefined;
    try {
      const j = await res.json();
      msg = j?.error || msg;
      if (typeof j?.retryAfterMs === "number") retryAfterMs = j.retryAfterMs;
    } catch { /* ignore */ }
    if (res.status === 503 && retryAfterMs) {
      const seconds = Math.max(1, Math.ceil(retryAfterMs / 1000));
      const err = new Error(`Lightspeed is tijdelijk overbelast — probeer over ${seconds} seconden opnieuw.`);
      (err as any).code = "LIGHTSPEED_RATE_LIMITED";
      (err as any).retryAfterMs = retryAfterMs;
      throw err;
    }
    throw new Error(msg);
  }

  const blob = await res.blob();
  if (blob.type && !blob.type.includes("pdf")) {
    if (preOpenedWindow) preOpenedWindow.close();
    throw new Error("Onverwacht response-type van pakbon-functie");
  }
  const blobUrl = URL.createObjectURL(blob);
  const fileName = `pakbon-${orderid}.pdf`;
  const cleanup = () => setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);

  // Helper: trigger native download via onzichtbare <a download>.
  const triggerDownload = () => {
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = fileName;
    a.rel = "noopener";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      try { document.body.removeChild(a); } catch { /* ignore */ }
    }, 0);
    cleanup();
  };

  // MOBIEL: native download via <a download>
  if (isMobileDevice()) {
    if (preOpenedWindow) {
      try { preOpenedWindow.close(); } catch { /* ignore */ }
    }
    triggerDownload();
    return;
  }

  // DESKTOP: gebruik pre-opened window indien beschikbaar (popup-safe)
  if (preOpenedWindow && !preOpenedWindow.closed) {
    preOpenedWindow.location.href = blobUrl;
    cleanup();
    return;
  }

  // Pre-open faalde / werd geblokkeerd: fallback naar a-download
  // (geen tweede window.open — die wordt sowieso geblokkeerd buiten de
  // user-gesture).
  triggerDownload();
}
